import React, { useState, useEffect, useContext, useMemo } from "react";
import { AuthContext } from "../../../contexts/AuthContext";
import Header from "../../../layout/Header/Header";
import { Button, ButtonGroup, Row, Col, Table, Input, Label } from "reactstrap";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import ExportJsonExcel from "js-export-excel";
import axios from "axios";
import { TableHeader, Pagination, Search } from "../../../DataTable";
import useFullPageLoader from "../../../hooks/useFullPageLoader";

function ListadoAnticiposOrdenesCompra() {
  const { user } = useContext(AuthContext);
  const URL_ORDENES_COMPRA = process.env.REACT_APP_URL_ORDENES_COMPRA;
  const URL_PROVEEDORES = process.env.REACT_APP_URL_PROVEEDORES;

  const [comments, setComments] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [selectedProveedor, setSelectedProveedor] = useState("");
  const [fechaInicio, setFechaInicio] = useState("");
  const [fechaFin, setFechaFin] = useState("");
  const [loader, showLoader, hideLoader] = useFullPageLoader();

  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [sorting, setSorting] = useState({ field: "", order: "" });

  const ITEMS_PER_PAGE = 50;

  useEffect(() => {
    showLoader();
    axios
      .get(URL_ORDENES_COMPRA, {
        headers: { Authorization: `Bearer: ${localStorage.getItem("app_token")}` },
      })
      .then((res) => {
        const all = res.data
          .filter((a) => a.is_active === "Si")
          .map((a) => {
            const total = parseFloat(a.total_general || 0);
            const anticipos = parseFloat(a.anticipos || 0);
            return {
              _id: a._id,
              idOrdenCompra: a.idOrdenCompra,
              fecha: a.fecha,
              proveedor: a.proveedores?.[0]?.nombre_comercial || "",
              codigoProveedor: a.proveedores?.[0]?.codigo,
              idProveedor: a.proveedores?.[0]?._id || "",
              total,
              anticipos,
              // saldo = total - anticipos (ambos campos vienen de la OC)
              saldo: total - anticipos,
            };
          });
        setComments(all);
        hideLoader();
      })
      .catch((err) => {
        console.log(err);
        hideLoader();
      });

    axios
      .get(URL_PROVEEDORES, {
        headers: { Authorization: `Bearer: ${localStorage.getItem("app_token")}` },
      })
      .then((res) => setProveedores(res.data))
      .catch((err) => console.log(err));
  }, []);

  const headers = [
    { name: "OC", field: "idOrdenCompra", sortable: true },
    { name: "Fecha", field: "fecha", sortable: true },
    { name: "Proveedor", field: "proveedor", sortable: true },
    { name: "Total", field: "total", sortable: true },
    { name: "Anticipos", field: "anticipos", sortable: true },
    { name: "Saldo", field: "saldo", sortable: true },
  ];

  const fmt = (n) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(
      Number(n) || 0
    );

  const filtradas = useMemo(() => {
    let computed = comments;
    if (selectedProveedor) computed = computed.filter((c) => c.idProveedor === selectedProveedor);
    if (fechaInicio) computed = computed.filter((c) => c.fecha >= fechaInicio);
    if (fechaFin) computed = computed.filter((c) => c.fecha <= fechaFin);
    if (search)
      computed = computed.filter(
        (c) =>
          c.proveedor.toLowerCase().includes(search.toLowerCase()) ||
          String(c.idOrdenCompra).includes(search)
      );
    return computed;
  }, [comments, search, selectedProveedor, fechaInicio, fechaFin]);

  const commentsData = useMemo(() => {
    let computed = [...filtradas];
    setTotalItems(computed.length);
    if (sorting.field) {
      const reversed = sorting.order === "asc" ? 1 : -1;
      computed.sort(
        (a, b) =>
          reversed *
          (typeof a[sorting.field] === "number"
            ? a[sorting.field] - b[sorting.field]
            : String(a[sorting.field]).localeCompare(String(b[sorting.field])))
      );
    }
    return computed.slice(
      (currentPage - 1) * ITEMS_PER_PAGE,
      (currentPage - 1) * ITEMS_PER_PAGE + ITEMS_PER_PAGE
    );
  }, [filtradas, currentPage, sorting]);

  const totales = useMemo(() => {
    return filtradas.reduce(
      (acc, c) => {
        acc.total += c.total;
        acc.anticipos += c.anticipos;
        acc.saldo += c.saldo;
        return acc;
      },
      { total: 0, anticipos: 0, saldo: 0 }
    );
  }, [filtradas]);

  function PDFTabla() {
    const doc = new jsPDF("landscape", "mm", "a4", true);
    const img = new Image();
    img.src = process.env.PUBLIC_URL + "Logo-Lynqro-PDF.png";
    doc.addImage(img, "png", 230, 30, 45, 15);
    const img2 = new Image();
    img2.src = process.env.PUBLIC_URL + "logoInstitucion.png";
    doc.addImage(img2, "png", 125, 10, 30, 30);
    doc.text(`Anticipos y Saldos por Orden de Compra`, 20, 40);
    doc.autoTable({
      head: [["OC", "Fecha", "Proveedor", "Total", "Anticipos", "Saldo"]],
      body: filtradas.map((c) => [
        c.idOrdenCompra,
        c.fecha,
        c.proveedor,
        fmt(c.total),
        fmt(c.anticipos),
        fmt(c.saldo),
      ]),
      startY: 45,
      foot: [["", "", "Total", fmt(totales.total), fmt(totales.anticipos), fmt(totales.saldo)]],
      footStyles: { fontSize: 11, fillColor: [255, 255, 255], textColor: [0, 0, 0] },
      showFoot: "lastPage",
    });
    doc.save(`AnticiposOrdenesCompra.pdf`);
  }

  function excel() {
    const dataExcel = filtradas.map((c) => ({
      OC: c.idOrdenCompra,
      Fecha: c.fecha,
      Proveedor: c.proveedor,
      Total: c.total,
      Anticipos: c.anticipos,
      Saldo: c.saldo,
    }));
    const option = {
      fileName: "AnticiposOrdenesCompra",
      datas: [
        {
          sheetData: dataExcel,
          sheetName: "AnticiposOC",
          sheetFilter: ["OC", "Fecha", "Proveedor", "Total", "Anticipos", "Saldo"],
          sheetHeader: ["OC", "Fecha", "Proveedor", "Total", "Anticipos", "Saldo"],
        },
      ],
    };
    new ExportJsonExcel(option).saveExcel();
  }

  return (
    <>
      <Header />
      {loader}
      <div className="card container col-sm-11">
        <Row>
          <Col sm={8}>
            <Button href="/MenuCXP" className="btn" color="secondary">
              Regresar
            </Button>
          </Col>
          <Col sm={4} align="right">
            <ButtonGroup id="logoutBoton">
              <Button size="sm" className="btn" color="danger" onClick={PDFTabla}>
                PDF <i className="far fa-file-pdf"></i>
              </Button>
              <Button size="sm" className="btn" color="primary" onClick={excel}>
                Excel <i className="far fa-file-excel"></i>
              </Button>
            </ButtonGroup>
          </Col>
        </Row>

        <h3 align="center">Anticipos y Saldos por Orden de Compra</h3>

        <Row>
          <Col md={3}>
            <Label>Proveedor</Label>
            <Input
              bsSize="sm"
              type="select"
              value={selectedProveedor}
              onChange={(e) => {
                setSelectedProveedor(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="">Todos</option>
              {proveedores
                .sort((a, b) => (a.nombre_comercial > b.nombre_comercial ? 1 : -1))
                .map((a) => (
                  <option key={a._id} value={a._id}>
                    {a.codigo} - {a.nombre_comercial}
                  </option>
                ))}
            </Input>
          </Col>
          <Col md={2}>
            <Label>Fecha Inicio</Label>
            <Input
              bsSize="sm"
              type="date"
              value={fechaInicio}
              onChange={(e) => {
                setFechaInicio(e.target.value);
                setCurrentPage(1);
              }}
            />
          </Col>
          <Col md={2}>
            <Label>Fecha Fin</Label>
            <Input
              bsSize="sm"
              type="date"
              value={fechaFin}
              onChange={(e) => {
                setFechaFin(e.target.value);
                setCurrentPage(1);
              }}
            />
          </Col>
          <Col md={{ size: 4, offset: 1 }} align="right">
            <Label>&nbsp;</Label>
            <Search
              onSearch={(value) => {
                setSearch(value);
                setCurrentPage(1);
              }}
            />
          </Col>
        </Row>
        <br />

        <Table size="sm" striped borderless className="table-responsive-xl">
          <TableHeader
            headers={headers}
            onSorting={(field, order) => setSorting({ field, order })}
          />
          <tbody>
            {commentsData.map((c) => (
              <tr key={c._id}>
                <td>{c.idOrdenCompra}</td>
                <td style={{ minWidth: 100 }}>{c.fecha}</td>
                <td>{c.proveedor}</td>
                <td style={{ textAlign: "right" }}>{fmt(c.total)}</td>
                <td style={{ textAlign: "right" }}>{fmt(c.anticipos)}</td>
                <td style={{ textAlign: "right", fontWeight: 600 }}>{fmt(c.saldo)}</td>
              </tr>
            ))}
            <tr>
              <td colSpan={3} className="negrita" style={{ textAlign: "right" }}>
                TOTAL
              </td>
              <td className="negrita" style={{ textAlign: "right" }}>{fmt(totales.total)}</td>
              <td className="negrita" style={{ textAlign: "right" }}>{fmt(totales.anticipos)}</td>
              <td className="negrita" style={{ textAlign: "right" }}>{fmt(totales.saldo)}</td>
            </tr>
          </tbody>
        </Table>

        <div className="col-md-6">
          <Pagination
            total={totalItems}
            itemsPerPage={ITEMS_PER_PAGE}
            currentPage={currentPage}
            onPageChange={(page) => setCurrentPage(page)}
          />
        </div>
      </div>
    </>
  );
}

export default ListadoAnticiposOrdenesCompra;
